import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InternationalMetaTagsManager from './InternationalMetaTagsManager';

const HowToPage: React.FC = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <InternationalMetaTagsManager pageType="howto" />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('howTo.title')}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t('howTo.subtitle')}
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8 border border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
              {t('howTo.introText')}
            </p>
          </div>

          {/* Steps Section */}
          <div className="space-y-8">
            {/* Step 1 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 border border-gray-200 dark:border-gray-700">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="bg-blue-600 rounded-full w-12 h-12 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">1</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t('howTo.step1Title')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                    {t('howTo.step1Text')}
                  </p>
                  <div className="mt-4 bg-blue-900/50 border border-blue-700 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-blue-600 dark:text-blue-300 font-medium">Pro Tip:</span>
                    </div>
                    <p className="text-blue-700 dark:text-blue-200 mt-2">
                      Look for recent inspection dates - more recent inspections provide better insight into current conditions.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 border border-gray-200 dark:border-gray-700">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="bg-green-600 rounded-full w-12 h-12 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">2</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t('howTo.step2Title')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                    {t('howTo.step2Text')}
                  </p>
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div className="bg-green-900/50 border border-green-700 rounded-lg p-4">
                      <h4 className="font-semibold text-green-300 mb-2">Good Signs</h4>
                      <ul className="text-green-200 space-y-1">
                        <li>• Consistent high ratings</li>
                        <li>• Improving scores over time</li>
                        <li>• Recent positive inspections</li>
                      </ul>
                    </div>
                    <div className="bg-red-900/50 border border-red-700 rounded-lg p-4">
                      <h4 className="font-semibold text-red-300 mb-2">Warning Signs</h4>
                      <ul className="text-red-200 space-y-1">
                        <li>• Declining ratings</li>
                        <li>• Repeated violations</li>
                        <li>• Long gaps between inspections</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 border border-gray-200 dark:border-gray-700">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="bg-purple-600 rounded-full w-12 h-12 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">3</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t('howTo.step3Title')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                    {t('howTo.step3Text')}
                  </p>
                  <div className="mt-4 bg-purple-900/50 border border-purple-700 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-300 mb-2">What to Look For in Reports</h4>
                    <ul className="text-purple-200 space-y-1">
                      <li>• Recent date of experience</li>
                      <li>• Specific details about issues</li>
                      <li>• Multiple similar reports</li>
                      <li>• Severity of reported problems</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 border border-gray-200 dark:border-gray-700">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="bg-orange-600 rounded-full w-12 h-12 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">4</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t('howTo.step4Title')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                    {t('howTo.step4Text')}
                  </p>
                  <div className="mt-4 grid md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-200 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                      <div className="text-2xl mb-2">🍕</div>
                      <h5 className="font-semibold text-gray-900 dark:text-white">Fast Food</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Higher turnover, check freshness</p>
                    </div>
                    <div className="text-center p-4 bg-gray-200 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                      <div className="text-2xl mb-2">🍣</div>
                      <h5 className="font-semibold text-gray-900 dark:text-white">Raw Foods</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Extra attention to handling</p>
                    </div>
                    <div className="text-center p-4 bg-gray-200 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                      <div className="text-2xl mb-2">🥘</div>
                      <h5 className="font-semibold text-gray-900 dark:text-white">Buffets</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Temperature control critical</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Signs Section */}
          <div className="mt-12 bg-red-900/50 border border-red-700 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-300 mb-6">
              {t('howTo.warningSignsTitle')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-red-200">{t('howTo.warningSign1')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-red-200">{t('howTo.warningSign2')}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-red-200">{t('howTo.warningSign3')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-red-200">{t('howTo.warningSign4')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Tips Section */}
          <div className="mt-8 bg-green-900/50 border border-green-700 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-green-300 mb-6">
              {t('howTo.tipsTitle')}
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-green-400 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-green-200">{t('howTo.tip1')}</span>
              </div>
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-green-400 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-green-200">{t('howTo.tip2')}</span>
              </div>
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-green-400 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-green-200">{t('howTo.tip3')}</span>
              </div>
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-green-400 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-green-200">{t('howTo.tip4')}</span>
              </div>
            </div>
          </div>

          {/* Keywords for SEO */}
          <div className="hidden">
            <span>{t('seo.keywords.foodSafety')}</span>
            <span>{t('seo.keywords.restaurantHygiene')}</span>
            <span>{t('seo.keywords.foodInspection')}</span>
            <span>{t('seo.keywords.belgiumRestaurants')}</span>
            <span>how to check restaurant safety</span>
            <span>restaurant hygiene guide</span>
            <span>food safety tips</span>
            <span>restaurant inspection guide</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default HowToPage;